import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { SelectField } from "../../shared/ui/SelectField"
import {
  previewNotificationAudienceApi,
  sendBroadcastApi,
  NotificationFilters,
  NotificationCampaign,
} from "../../features/notifications/api/notifications.api"
import {
  UsersIcon,
  CalendarDaysIcon,
  IdentificationIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  XMarkIcon,
  ChevronRightIcon,
  MegaphoneIcon,
} from "@heroicons/react/16/solid"

const ROLE_OPTIONS = [
  { value: "", label: "Любая роль" },
  { value: "user", label: "Пользователь" },
  { value: "mentor", label: "Ментор" },
  { value: "moderator", label: "Модератор" },
  { value: "admin", label: "Админ" },
]

const Field = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">{label}</label>
    {children}
  </div>
)

export const BroadcastPage = () => {
  const [filters, setFilters] = useState<NotificationFilters>({})
  const [audience, setAudience] = useState<number | null>(null)
  const [counting, setCounting] = useState(false)

  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [dataPairs, setDataPairs] = useState<{ key: string; value: string }[]>([])

  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState("")
  const [lastResult, setLastResult] = useState<NotificationCampaign | null>(null)

  const hasFilters = !!(filters.inactive_days || filters.role)

  useEffect(() => {
    let cancelled = false
    setCounting(true)
    const t = setTimeout(async () => {
      try {
        const res = await previewNotificationAudienceApi(filters)
        if (!cancelled) setAudience(res.data.count)
      } catch {
        if (!cancelled) setAudience(null)
      } finally {
        if (!cancelled) setCounting(false)
      }
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
  }, [filters])

  const addDataPair = () => setDataPairs((p) => [...p, { key: "", value: "" }])
  const updateDataPair = (i: number, patch: Partial<{ key: string; value: string }>) =>
    setDataPairs((p) => p.map((row, idx) => (idx === i ? { ...row, ...patch } : row)))
  const removeDataPair = (i: number) => setDataPairs((p) => p.filter((_, idx) => idx !== i))

  const canSend = title.trim() && body.trim() && !sending

  const handleSend = async () => {
    if (!canSend) return
    setSending(true)
    setSendError("")
    try {
      const data = dataPairs.reduce<Record<string, string>>((acc, { key, value }) => {
        if (key.trim()) acc[key.trim()] = value
        return acc
      }, {})
      const res = await sendBroadcastApi({
        title: title.trim(),
        body: body.trim(),
        filters,
        ...(Object.keys(data).length ? { data } : {}),
      })
      setLastResult(res.data)
      setTitle("")
      setBody("")
      setDataPairs([])
    } catch {
      setSendError("Не удалось отправить рассылку")
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

      {/* ── Filters ── */}
      <div className="lg:col-span-1 bg-white rounded-xl border p-5 space-y-4 h-fit">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-gray-400" />
          Аудитория
        </h2>

        <Field label="Неактивен, дней">
          <input
            type="number"
            min={0}
            value={filters.inactive_days ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, inactive_days: e.target.value ? Number(e.target.value) : undefined }))}
            placeholder="например, 30"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
          />
        </Field>

        <Field label={<span className="flex items-center gap-1.5"><IdentificationIcon className="w-3.5 h-3.5" />Роль</span>}>
          <SelectField
            value={filters.role ?? ""}
            onChange={(v) => setFilters((f) => ({ ...f, role: (v as NotificationFilters["role"]) || undefined }))}
            options={ROLE_OPTIONS}
          />
        </Field>

        {hasFilters && (
          <button
            type="button"
            onClick={() => setFilters({})}
            className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
          >
            Сбросить фильтры
          </button>
        )}

        <div className="border-t pt-4">
          <div className="flex items-center justify-between rounded-xl bg-blue-50/60 border border-blue-100 px-4 py-3.5">
            <div>
              <p className="text-xs text-blue-600/70 font-medium">Получат уведомление</p>
              <p className="text-2xl font-bold text-blue-700 mt-0.5 tabular-nums">
                {counting ? <span className="inline-block w-10 h-6 bg-blue-100 rounded animate-pulse" /> : audience ?? "—"}
              </p>
            </div>
            <UsersIcon className="w-8 h-8 text-blue-300" />
          </div>
          <p className="text-[11px] text-gray-400 mt-2">Забаненные и удалённые пользователи исключаются автоматически.</p>
        </div>
      </div>

      {/* ── Composer ── */}
      <div className="lg:col-span-2 bg-white rounded-xl border p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <MegaphoneIcon className="w-4 h-4 text-gray-400" />
          Сообщение
        </h2>

        <Field label="Заголовок">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Соскучились?"
            maxLength={120}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
          />
        </Field>

        <Field label="Текст">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Новые курсы уже ждут вас"
            rows={3}
            maxLength={500}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300 resize-none"
          />
        </Field>

        {/* Push preview card */}
        {(title.trim() || body.trim()) && (
          <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
              <img src="/icon.png" alt="" className="w-6 h-6 rounded-md object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{title.trim() || "Заголовок уведомления"}</p>
              <p className="text-xs text-gray-500 line-clamp-2">{body.trim() || "Текст уведомления"}</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Данные (необязательно)</p>
            <button type="button" onClick={addDataPair} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
              <PlusIcon className="w-3.5 h-3.5" />
              Добавить поле
            </button>
          </div>
          {dataPairs.length > 0 && (
            <div className="space-y-2">
              {dataPairs.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={row.key}
                    onChange={(e) => updateDataPair(i, { key: e.target.value })}
                    placeholder="screen"
                    className="w-1/3 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
                  />
                  <input
                    value={row.value}
                    onChange={(e) => updateDataPair(i, { value: e.target.value })}
                    placeholder="courses_catalog"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
                  />
                  <button type="button" onClick={() => removeDataPair(i)} className="p-2 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {sendError && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            {sendError}
          </div>
        )}

        {lastResult && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-2">
            <p className="text-sm font-medium text-emerald-700 flex items-center gap-1.5">
              <CheckCircleIcon className="w-4 h-4" />
              Рассылка «{lastResult.title}» отправлена
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded-lg py-2 border border-emerald-100">
                <p className="text-lg font-bold text-gray-800 tabular-nums">{lastResult.target_count}</p>
                <p className="text-[11px] text-gray-400">аудитория</p>
              </div>
              <div className="bg-white rounded-lg py-2 border border-emerald-100">
                <p className="text-lg font-bold text-emerald-600 tabular-nums">{lastResult.sent_count}</p>
                <p className="text-[11px] text-gray-400">доставлено</p>
              </div>
              <div className="bg-white rounded-lg py-2 border border-emerald-100">
                <p className="text-lg font-bold text-red-500 tabular-nums">{lastResult.failed_count}</p>
                <p className="text-[11px] text-gray-400">не доставлено</p>
              </div>
            </div>
            <Link to="/admin/notifications/history" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
              Смотреть в истории
              <ChevronRightIcon className="w-3 h-3" />
            </Link>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button
            disabled={!canSend}
            onClick={handleSend}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
          >
            {sending
              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <PaperAirplaneIcon className="w-4 h-4" />}
            {sending ? "Отправка…" : `Отправить${audience !== null ? ` (${audience})` : ""}`}
          </button>
        </div>
        {sending && (
          <p className="text-[11px] text-gray-400 text-right flex items-center justify-end gap-1">
            <CalendarDaysIcon className="w-3 h-3" />
            Большие сегменты отправляются несколько секунд — не закрывайте страницу
          </p>
        )}
      </div>
    </div>
  )
}
